import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { errText } from "@/lib/api";
import { TIERS } from "@/lib/i18n";
import { useEvaluation } from "./EvaluatorApp";
import { EvaluatorHeader, JobButton } from "./EvaluatorHeader";

export default function ReviewPage() {
  const { ctx, token, submit } = useEvaluation();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const missing = ctx.candidates.filter((c) => c.progress.status !== "COMPLETED");
  const priorityMissing = !ctx.category_ranking_complete;
  const critMap = Object.fromEntries(ctx.criteria.map((c) => [c.id, c]));
  const scored = Object.values(ctx.evaluations).reduce((s, e) => s + Object.values(e.criteria || {}).filter((x) => x.score != null || x.evidence_status === "INSUFFICIENT").length, 0);
  const confirm = async () => {
    setBusy(true);
    try { await submit(); nav(`/e/${token}/done`); } catch (e) { toast.error(errText(e)); } finally { setBusy(false); setOpen(false); }
  };
  return (
    <div className="min-h-screen">
      <EvaluatorHeader right={<JobButton />} />
      <main className="fade-up mx-auto max-w-3xl px-4 py-10 sm:px-6">
        <p className="eyebrow mb-2">Son adım</p>
        <h1 className="font-heading text-2xl font-bold tracking-tight sm:text-3xl">Değerlendirmenizi Gözden Geçirin</h1>
        <p className="mt-3 flex items-center gap-2 text-base" data-testid="review-progress">
          {ctx.progress.evaluated} / {ctx.progress.total} aday değerlendirildi {missing.length === 0 && <Check className="h-4 w-4 text-plena-success" />}
        </p>

        {priorityMissing && (
          <div className="mt-6 rounded-2xl bg-[#FDF4DC] px-6 py-5" data-testid="review-priority-missing">
            <p className="font-medium text-[#8A6414]">Kategori önceliklendirmesi tamamlanmadı</p>
            <Button variant="outline" data-testid="complete-priorities-button" onClick={() => nav(`/e/${token}/priorities`)} className="mt-3 rounded-full bg-white">Kategorileri Önceliklendir</Button>
          </div>
        )}

        {missing.length > 0 && (
          <div className="mt-6 rounded-2xl bg-[#FDF4DC] px-6 py-5" data-testid="review-missing">
            <p className="font-medium text-[#8A6414]">{missing.length} eksik değerlendirme var</p>
            <ul className="mt-2 space-y-1 text-sm text-[#8A6414]">{missing.map((c) => <li key={c.id}>• {c.display_name} · {c.progress.done}/{c.progress.total} kategori</li>)}</ul>
            <Button variant="outline" data-testid="complete-missing-button" onClick={() => nav(`/e/${token}/candidates/${missing[0].id}`)} className="mt-4 rounded-full bg-white">Eksikleri Tamamla</Button>
          </div>
        )}

        <section className="mt-10">
          <h2 className="mb-3 font-heading text-base font-semibold md:text-lg">Final sıralamanız</h2>
          <ol className="plena-card divide-y divide-plena-border" data-testid="final-ranking-list">
            {ctx.candidates.map((c, i) => (
              <li key={c.id} className="flex items-center gap-4 px-5 py-3.5" data-testid={`final-rank-${c.id}`}>
                <span className="w-6 font-heading text-base font-bold tabular-nums text-plena-muted">{i + 1}</span>
                <div className="min-w-0 flex-1"><p className="truncate font-medium">{c.display_name}</p>{c.title && <p className="truncate text-xs text-plena-text2">{c.title}</p>}</div>
                <span className="text-sm text-plena-text2" data-testid={`final-overall-${c.id}`}>{ctx.evaluations[c.id]?.overall_score ?? "—"} / 100</span>
              </li>
            ))}
          </ol>
          <Button variant="ghost" size="sm" data-testid="edit-ranking-button" onClick={() => nav(`/e/${token}/ranking`)} className="mt-3 rounded-full text-plena-text2"><ArrowLeft className="mr-1.5 h-3.5 w-3.5" /> Sıralamayı düzenle</Button>
        </section>

        <section className="mt-10">
          <h2 className="mb-3 font-heading text-base font-semibold md:text-lg">Kategori önceliğiniz</h2>
          <ol className="plena-card divide-y divide-plena-border" data-testid="final-priority-list">
            {ctx.category_ranking.map((r, i) => critMap[r.criterion_id] && (
              <li key={r.criterion_id} className="flex items-center gap-4 px-5 py-2.5 text-sm">
                <span className="w-6 font-heading font-bold tabular-nums text-plena-muted">{i + 1}</span>
                <span className="flex-1">{critMap[r.criterion_id].name}</span>
                <span className="text-xs text-plena-text2">{TIERS[r.tier] || "—"}</span>
              </li>
            ))}
          </ol>
          <Button variant="ghost" size="sm" data-testid="edit-priorities-button" onClick={() => nav(`/e/${token}/priorities`)} className="mt-3 rounded-full text-plena-text2"><ArrowLeft className="mr-1.5 h-3.5 w-3.5" /> Önceliği düzenle</Button>
        </section>

        <section className="mt-10 rounded-2xl bg-plena-surface2 px-6 py-5 text-sm">
          <p className="font-medium">Değerlendirme özeti</p>
          <ul className="mt-2 space-y-1 text-plena-text2">
            <li>• {ctx.progress.total} aday</li>
            <li>• {scored} kategori puanı</li>
            <li>• {missing.length === 0 ? "Final sıralama tamamlandı" : "Final sıralama eksik değerlendirmeler tamamlanınca onaylanabilir"}</li>
            <li>• {priorityMissing ? "Kategori önceliği eksik" : "Kategori önceliği tamamlandı"}</li>
          </ul>
        </section>

        <div className="mt-10 flex justify-end">
          <Button data-testid="submit-evaluation-button" disabled={missing.length > 0 || priorityMissing} onClick={() => setOpen(true)} className="btn-press h-12 rounded-full px-7 text-base font-semibold">Değerlendirmeyi Tamamla</Button>
        </div>
      </main>

      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent className="rounded-2xl" data-testid="submit-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Değerlendirmenizi tamamlamak üzeresiniz.</AlertDialogTitle>
            <AlertDialogDescription>Gönderim sonrasında değerlendirmeleriniz değiştirilemeyecektir.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="submit-cancel-button" className="rounded-full">Geri Dön</AlertDialogCancel>
            <AlertDialogAction data-testid="submit-confirm-button" disabled={busy} onClick={(e) => { e.preventDefault(); confirm(); }} className="rounded-full">{busy ? "Gönderiliyor…" : "Onayla ve Tamamla"}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
